import URI from "urijs";
import { parseSearchParams } from ".";
import {
  PDE,
  PrescriptionDrug,
  CoverageInfo,
  BeneficiaryAndApiHeaders,
  Beneficiary,
  IdParts,
} from "../@types";
import { getBeneInfo, getCSRBeneInfo } from "../api";
import { uppercaseFirst } from "./prescriptionHelpers";

// Intended to be used like so: call iniitateLogin() -> redirect to MBP & SLS for login ->
export async function getBeneficiaryInfo(
  code: string
): Promise<BeneficiaryAndApiHeaders> {
  // Ask the API to kindly fetch the details of our beneficiary
  const beneInfo = await getBeneInfo(code);

  return beneInfo;
}

export async function getBeneInfoForCSR(
  code: string
): Promise<BeneficiaryAndApiHeaders> {
  return await getCSRBeneInfo(code);
}

export function getSLSCode(search: string): string {
  const { code } = parseSearchParams(URI.parseQuery(search));
  return code;
}

export const doesNotInclude = (
  acc: PrescriptionDrug[],
  { ndc, name }: { ndc: string; name: string }
): boolean => {
  return acc.every(
    ({ ndc: currentNDC, name: currentName }) =>
      currentNDC !== ndc && currentName !== name
  );
};

export const extractPDEsFromBene = ({
  pdes,
}: {
  pdes: PDE[];
}): PrescriptionDrug[] => {
  const isWithinLastYear = (service_date: string): boolean => {
    const pdeDate = new Date(service_date).getTime();
    const thisYear = new Date().getFullYear();
    const aYearAgo = new Date().setFullYear(thisYear - 1);

    if (pdeDate > aYearAgo) {
      return true;
    }

    return false;
  };

  return pdes.reduce((accumulator, currentPDE) => {
    if (
      /* istanbul ignore next */
      currentPDE.service_date // FIXME: Lovely little hack because the fake data currently doesn't include a service date.
        ? isWithinLastYear(currentPDE.service_date)
        : true
    ) {
      const theDrug = currentPDE.drug;
      if (theDrug.name !== "" && doesNotInclude(accumulator, theDrug)) {
        accumulator.push(theDrug);
      }
    }
    return accumulator;
  }, [] as PrescriptionDrug[]);
};

interface PlanIdParts extends IdParts {
  contract_year: string;
}

// todo: rename this to arePlansTheSame
export const isCurrentPlan = (
  coverageInfo: CoverageInfo | undefined,
  plan: PlanIdParts
): boolean => {
  if (!coverageInfo) return false;

  return (
    coverageInfo.plan_id === plan.plan_id &&
    coverageInfo.segment_id === plan.segment_id &&
    coverageInfo.contract_id === plan.contract_id &&
    (coverageInfo.contract_year === plan.contract_year ||
      coverageInfo.coverage_year === plan.contract_year)
  );
};

export const deduplicatePrescriptions = (
  prescriptions: PrescriptionDrug[]
): PrescriptionDrug[] => {
  return prescriptions.filter(
    (a, index) =>
      prescriptions.findIndex(b => {
        return (
          a.ndc === b.ndc &&
          a.package_type === b.package_type &&
          a.quantity === b.quantity &&
          a.frequency === b.frequency
        );
      }) === index
  );
};

export const getBeneFirstAndLastName = (
  beneficiary: Beneficiary,
  t: (key: string) => string
): string => {
  const firstName =
    beneficiary.firstname && beneficiary.firstname.length
      ? uppercaseFirst(beneficiary.firstname[0])
      : "";
  return firstName || beneficiary.lastname
    ? `${firstName} ${uppercaseFirst(beneficiary.lastname)}`.trim()
    : t("bene_landing.name_not_available");
};

export const getBeneFirstNameAndLastInitial = (
  beneficiary: Beneficiary,
  t: (key: string) => string
): string => {
  const firstName =
    beneficiary.firstname && beneficiary.firstname.length
      ? uppercaseFirst(beneficiary.firstname[0])
      : "";
  if (firstName && beneficiary.lastname) {
    return `${firstName} ${beneficiary.lastname[0].toUpperCase()}.`.trim();
  } else if (beneficiary.lastname) {
    return uppercaseFirst(beneficiary.lastname).trim();
  } else if (firstName) {
    return firstName;
  } else {
    return t("bene_landing.name_not_available");
  }
};
